/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import Switch, { Case, Default } from "react-switch-case"
import Loading from "@medaica/common/components/loading"
import { logError } from "@medaica/common/services/util"
import { ThemeProvider } from "@mui/material/styles"
import bookingTheme from "../booking/booking-theme"
import HeartReportLayout from "./components/heart-report-layout"
import { HeartReportContextProvider, useHeartReportStores } from "./heart-report-context"
import RequestCode from "./views/request-code"
import Verify from "./views/verify"
import Download from "./views/download"
import Unavailable from "./views/unavailable"

/**
 * The URL carries the emailed link token, so no request from this page may leak it in a
 * Referer header (to the API, or to any linked site). A `<meta name="referrer">` added at
 * mount applies to every request the page makes afterwards; it is removed on unmount so the
 * rest of the portal keeps its default policy.
 */
const useNoReferrer = (): void => {
  useEffect(() => {
    const meta = document.createElement("meta")
    meta.name = "referrer"
    meta.content = "no-referrer"
    document.head.appendChild(meta)
    return () => {
      meta.remove()
    }
  }, [])
}

const HeartReportAppInner = observer((): ReactElement => {
  const { uiStore, sessionStore } = useHeartReportStores()
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    const init = async (): Promise<void> => {
      try {
        // Picks the first screen from the link token and any session cached for it. Unlike the
        // booking wizard this sends NOTHING on load: the code goes out only when the patient
        // clicks "Send me a code" (email security scanners open links, and an automatic send
        // would burn the patient's send allowance).
        await sessionStore.init()
      } catch (error) {
        logError(error)
        uiStore.goTo("requestCode")
      } finally {
        setInitializing(false)
      }
    }
    void init()
    return () => sessionStore.dispose()
  }, [sessionStore, uiStore])

  if (initializing) {
    return (
      <HeartReportLayout>
        <div className="py-24">
          <Loading />
        </div>
      </HeartReportLayout>
    )
  }

  return (
    <Switch condition={uiStore.view}>
      <Case value="requestCode">
        <RequestCode />
      </Case>
      <Case value="verify">
        <Verify />
      </Case>
      <Case value="download">
        <Download />
      </Case>
      <Case value="unavailable">
        <Unavailable />
      </Case>
      <Default>
        <RequestCode />
      </Default>
    </Switch>
  )
})

/**
 * `/heart-report?t=<token>` — the page the patient's report-ready email
 * links to. The token is read from the URL exactly once, when the page mounts, and
 * handed to the store; it is never re-read or sent as a query string to the API.
 */
const HeartReportApp = (): ReactElement => {
  const [token] = useState(() => new URLSearchParams(window.location.search).get("t"))
  useNoReferrer()

  return (
    <ThemeProvider theme={bookingTheme}>
      <HeartReportContextProvider token={token}>
        <HeartReportAppInner />
      </HeartReportContextProvider>
    </ThemeProvider>
  )
}

export default HeartReportApp
